import { useEditorState, type Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef, useState } from "react";

interface TOCItem {
  id: string;
  level: number;
  text: string;
  pos: number;
}

interface TableOfContentsProps {
  editor: Editor;
}

function extractHeadings(editor: Editor): TOCItem[] {
  const headings: TOCItem[] = [];
  const doc = editor.state.doc;

  doc.descendants((node, pos) => {
    if (node.type.name === "heading") {
      const level = node.attrs.level as number;
      const text = node.textContent;
      // Use position as a stable identifier since headings don't have
      // built-in IDs in the default Tiptap schema.
      const id = `toc-${pos}`;

      if (text.trim()) {
        headings.push({ id, level, text, pos });
      }
    }
  });

  return headings;
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + "...";
}

export function TableOfContents({ editor }: TableOfContentsProps) {
  const [activePos, setActivePos] = useState<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const headings = useEditorState({
    editor,
    selector: ({ editor: e }) => extractHeadings(e),
  });

  // Track which heading is currently visible in the viewport.
  // We observe each heading DOM element and mark the first visible one as active.
  useEffect(() => {
    if (!headings || headings.length === 0) return;

    const editorElement = editor.view.dom;
    const headingElements = editorElement.querySelectorAll("h1, h2, h3");

    if (headingElements.length === 0) return;

    // Clean up any previous observer
    observerRef.current?.disconnect();

    const visiblePositions = new Set<number>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Find the matching heading by index, since the DOM order matches
          // the document order.
          const index = Array.from(headingElements).indexOf(
            entry.target as HTMLElement,
          );
          if (index >= 0 && index < headings.length) {
            const heading = headings[index];
            if (entry.isIntersecting) {
              visiblePositions.add(heading.pos);
            } else {
              visiblePositions.delete(heading.pos);
            }
          }
        }

        // Pick the first visible heading (by document position)
        const sorted = headings
          .filter((h) => visiblePositions.has(h.pos))
          .sort((a, b) => a.pos - b.pos);

        if (sorted.length > 0) {
          setActivePos(sorted[0].pos);
        }
      },
      { rootMargin: "-10% 0px -60% 0px", threshold: 0 },
    );

    for (const el of headingElements) {
      observerRef.current.observe(el);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [editor, headings]);

  const scrollToHeading = useCallback(
    (pos: number) => {
      editor.chain().focus().setTextSelection(pos).run();

      // After setting the selection, scroll the resolved DOM element into view.
      const resolvedPos = editor.state.doc.resolve(pos);
      const domNode = editor.view.nodeDOM(resolvedPos.before());
      if (domNode instanceof HTMLElement) {
        domNode.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    [editor],
  );

  if (!headings || headings.length === 0) return null;

  return (
    <nav className="flex flex-col gap-0.5" aria-label="Table of contents">
      <span className="font-body text-xs text-beige uppercase tracking-wider mb-1">
        Contents
      </span>
      {headings.map((heading) => {
        const indent =
          heading.level === 1
            ? "pl-0"
            : heading.level === 2
              ? "pl-3"
              : "pl-6";
        const isActive = activePos === heading.pos;

        return (
          <button
            key={heading.id}
            type="button"
            onClick={() => scrollToHeading(heading.pos)}
            title={heading.text}
            className={`text-left text-sm font-body py-1 px-2 rounded transition-colors truncate ${indent} ${
              isActive
                ? "text-orange bg-orange/5"
                : "text-grey hover:text-black hover:bg-beige/10"
            }`}
          >
            {truncate(heading.text, 50)}
          </button>
        );
      })}
    </nav>
  );
}
